/*
 * File: index.ts
 * File Created: 2024-05-23 02:51:41
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-02-25 08:43:36
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import Vue from "vue";
import Router, { Route } from "vue-router";
import { RouteConfig } from "vue-router";
import { BehaviorSubject } from "rxjs";
import { observeOn, filter, first } from "rxjs/operators";
import { AuthPluginData } from "./../private/plugins/authentication";
import { VueHolder, isObjectEmpty } from "./../utilities";
import RouteTransition from "@/../components/transitions/route-transition/route-transition.vue";
import { PermissionHelper } from "@root/core/utilities/permission-helper";
import { IAllLang } from "../i18n/core";
import { IRegisterRouter } from "./core";

Vue.use(Router);

export interface IRegisterRouterInput extends IRegisterRouter {
    component: any;
}

const routes: RouteConfig[] = [];

export function RegisterRouter(
    config: Omit<IRegisterRouter, "name"> & { name: keyof IAllLang },
);
export function RegisterRouter(config: IRegisterRouter);
export function RegisterRouter(config) {
    return (component?) => {
        let input: IRegisterRouterInput = {
            ...config,
            component,
        };
        routeCache.push(input);
        resolveRoute(input);
    };
}
/// Use this to register custom login router
let loginRoute: IRegisterRouterInput;
export function RegisterLoginRouter(config: IRegisterRouter) {
    return (component?) => {
        let input: IRegisterRouterInput = {
            ...config,
            component,
            isLoginRoute: true,
        };
        loginRoute = input;

        return RegisterRouter(config)(component);
    };
}

/// FindLogin ////////////////////////
export function FindLoginRouter(): IRegisterRouterInput {
    /// todo
    return loginRoute;
}
/// FindRouter ///////////////////////
const routeCache: IRegisterRouterInput[] = [];
export function FindRouter(
    options: Partial<IRegisterRouterInput>,
): IRegisterRouterInput[] {
    return routeCache.filter((route) => {
        let keys = Object.keys(options);
        for (let key of keys) {
            /// if key === "path", for route[key] & options[key],
            /// if route[key] is `/route/child/:id`,
            /// and options[key] is `/route/child/1`,
            /// they should match and return true.

            if (key === "path" && matchDynamicSegmentPath(route[key], options[key]))
                continue;
            if (route[key] !== options[key]) return false;
        }
        return true;
    });
}
/// FindHome ///////////////////////
export function FindHome(): IRegisterRouterInput {
    const vue = VueHolder();
    let home = routeCache.reduce<IRegisterRouterInput[]>((final, route) => {
        /// must not be login route, and has default.
        if (route.isLoginRoute || !route.default) return final;
        if (PermissionHelper.check.call(vue, route.default)) final.push(route);
        return final;
    }, []);
    //if (home.length > 0) return home[home.length - 1];
    if (home.length > 0) return home[0];
    return FindRouter({ path: "/" })[0];
}
//////////////////////////////////////
const defComponent = (path) => ({
    render: (c) => c("router-view", { props: { path } }),
});
const autoRouteComponent = (path, comp) => {
    return {
        render: (c) => {
            let element = c(RouteTransition, { props: { path } }, [
                c("div", { key: "main" }, [c(comp)]),
            ]);
            // let element = c("div", { key: "main" }, [c(comp)]);
            return element;
        },
    };
};
function resolveRoute(config: IRegisterRouterInput) {
    let { path, name, component, redirect, meta } = config;
    // bind redirect function
    if (typeof redirect === "function") redirect = redirect.bind(VueHolder());

    let insertRoute = (routes: RouteConfig[], paths: string[], level: number = 0) => {
        if (paths.length === 0) return;
        let last = paths.length === level + 1;
        let path = paths[level];
        /// find the component
        let routePath = paths.slice(0, level + 1).join("");
        let routers = FindRouter({ path: routePath });
        //let comp = component || (routers.length === 0 ? null : routers[0].component);
        /// Val: fix router components. find the last overwritten route
        let comp = routers.length === 0 ? null : routers[routers.length - 1].component;
        /// find the route
        let idx = routes.findIndex(
            (value) => value.path === path || value.path === path.replace(/^\//, ""),
        );
        if (last) {
            let data = {
                path: level === 0 ? `${path}` : path.replace(/^\//, ""),
                name,
                redirect,
                meta,
                component: comp || defComponent(routePath),
            };
            if (idx === -1) routes.push(data as any);
            else {
                let route = routes[idx];
                Object.assign(route, data);
            }
            return;
        } else {
            let data = {
                path: level === 0 ? `${path}` : path.replace(/^\//, ""),
                name: path,
                redirect,
                component: comp
                    ? autoRouteComponent(routePath, comp)
                    : defComponent(routePath),
                children: [],
            };
            let route: RouteConfig = data;
            if (idx === -1) routes.push(data);
            else {
                route = routes[idx];
                Object.assign(route, { component: data.component });
                if (!route.children) route.children = [];
            }

            if (paths.length <= level + 1) return;
            insertRoute(route.children as any, paths, level + 1);
        }
    };

    /// 1) split paths
    let paths = path.match(/\/([^\/]+|$)/g);
    if (paths == null) {
        console.error(`<RegisterRouter> not support path without prefix "/". "${path}"`);
        return;
    }
    insertRoute(routes, paths as any);
}

const sjDefaultRoute: BehaviorSubject<any> = new BehaviorSubject(null);
export const obDefaultRoute = sjDefaultRoute.asObservable();
export default new Promise<Router>((resolve) => {
    setTimeout(() => {
        let router = new Router({
            mode: "history",
            linkActiveClass: "open active",
            base: process.env.BASE_URL,
            routes,
        });
        resolve(router);
        sjDefaultRoute.next(router);

        initGuards(router);
    }, 0);
});

function initGuards(baseRouter: Router) {
    let loginRouter = FindLoginRouter();
    let loginPath = loginRouter.path;

    baseRouter.beforeEach(async (to, from, next) => {
        const goNext = (router?: IRegisterRouterInput) => {
            do {
                if (!router) break;
                const obTheme: any = (baseRouter.app.$observables || {}).$theme;
                if (!obTheme) break;
                /// change container if definition exists
                const cont = router.container;
                if (!cont) break;
                obTheme.next(cont);
            } while (0);
            next();
        };
        do {
            let toPath = to.path;
            const vue = VueHolder();

            /// 1) allow login page
            if (loginPath === toPath) return goNext();

            /// 2) find the related router
            let routers = FindRouter({ path: toPath });
            if (routers.length === 0) break;
            let router = routers[routers.length - 1];

            /// 2.1) passing allow true
            if (router.allow === true) return goNext();

            /// 3) fetch permissions
            if (isObjectEmpty(AuthPluginData.permissions)) {
                try {
                    let data = await vue.$login();
                } catch (e) {
                    /// 3.1) if failed, redirect to login page
                    break;
                }
            }
            /// 3.2) check permission
            if (!PermissionHelper.authRouter.call(vue, router)) break;

            /// 3.3) if no component, find and goes to home.
            if (router.component == null) {
                router = FindHome();
                return next(router.path);
            }

            return goNext(router);
        } while (0);

        return next(loginPath);
    });
}

/**
 * Checks if an actual path matches a pattern path with dynamic segments.
 * @param {string} patternPath - The pattern path containing dynamic segments (e.g., `/route/child/:id`).
 * @param {string} actualPath - The actual path to match against the pattern (e.g., `/route/child/1`).
 * @returns {boolean} True if the actual path matches the pattern path, false otherwise.
 */
function matchDynamicSegmentPath(patternPath: string, actualPath: string | undefined) {
    // Split both paths into segments
    const patternSegments = patternPath.split("/");
    const actualSegments = (actualPath ?? "").split("/");

    // Paths with different numbers of segments do not match
    if (patternSegments.length !== actualSegments.length) {
        return false;
    }

    // Compare each segment
    for (let i = 0; i < patternSegments.length; i++) {
        const patternSegment = patternSegments[i];
        const actualSegment = actualSegments[i];

        // If the pattern segment is dynamic, skip to the next segment
        if (patternSegment.startsWith(":")) {
            continue;
        }

        // If the segments do not match, return false
        if (patternSegment !== actualSegment) {
            return false;
        }
    }

    // All segments match (with dynamic segments accounted for), so the paths match
    return true;
}
